/*
 * This hook deliberately imports useInfiniteQuery straight from
 * @tanstack/react-query instead of from app/ReactQuery. Both FOSSA wrappers,
 * useInfiniteQuery and useInfiniteQueryWithCursor, Omit getNextPageParam from
 * the options you may pass and hard-require a { results, pageSize, page } or a
 * { results, pageSize, last } body. This endpoint answers with a GraphQL-style
 * connection, { nodes, pageInfo }, so neither wrapper can express its paging.
 * Bypassing them also means get() no longer unwraps the body for us, which is
 * why fetchListFossabotPRs returns response.data itself. Please do not "fix"
 * this back to the wrapper.
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { stringify } from 'qs';
import type { QueryFunctionContext, QueryKey } from '@tanstack/react-query';

import { get } from '../../../redux/api';
import type { FossabotPRSort, FossabotPRVcsState } from '../../../../shared/fossabot/common';
import type { ListFossabotPRsResponse } from '../../../../shared/fossabot/response';
import type { ErrorResponse } from '../../../ReactQuery/errors';

/**
 * Rows fetched per page. The backend caps `first` at 100, and rejects the
 * request outright when neither `first` nor `last` is sent, so it always goes out.
 */
const PAGE_SIZE = 20;

/** Stands in for a 200 with no parseable body, so paging stops instead of throwing. */
const EMPTY_PR_CONNECTION: ListFossabotPRsResponse = {
  nodes: [],
  pageInfo: {
    startCursor: null,
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
  },
};

type ListFossabotPRsOptions = {
  /** The full project locator, e.g. `git+github.com/fossas/example`. */
  projectLocator: string;
  /** Filter to a single VCS state. The backend accepts one value only. */
  state?: FossabotPRVcsState;
  /** Server-side title search. An empty string means no filter. */
  search?: string;
  /**
   * Sort order. When omitted it is left out of the request entirely, all the
   * way through to the fossabot subgraph, so that subgraph's own default
   * ordering applies. Nothing in this repo pins what that ordering is.
   */
  sort?: FossabotPRSort;
};

async function fetchListFossabotPRs(
  { pageParam }: QueryFunctionContext<QueryKey, string>,
  { projectLocator, state, search, sort }: ListFossabotPRsOptions,
) {
  /*
   * qs skips undefined values and encodes by default, which project locators
   * need: they contain slashes and '+'. `after` is absent on the first page,
   * where react-query hands us an undefined pageParam.
   */
  const queryString = stringify(
    {
      projectLocator,
      first: PAGE_SIZE,
      after: pageParam,
      state,
      search: search || undefined,
      sort,
    },
    { addQueryPrefix: true },
  );
  const response = await get<ListFossabotPRsResponse>(`/api/fossabot/dependency-upgrade-prs${queryString}`);
  return response.data ?? EMPTY_PR_CONNECTION;
}

/**
 * Key for one project's PR list under one set of filters; changing any filter
 * changes the key, so the list refetches rather than showing stale rows.
 * An empty search collapses onto no search, matching what the backend does.
 */
export const getListFossabotPRsQueryKey = ({
  projectLocator,
  state,
  search,
  sort,
}: ListFossabotPRsOptions): QueryKey => [
  'fossabot',
  'dependency-upgrade-prs',
  projectLocator,
  'list',
  { state: state ?? null, search: search || null, sort: sort ?? null },
];

/**
 * Cursor-paginated list of a project's fossabot dependency-upgrade PRs.
 *
 * Call fetchNextPage to append a page, and read the rows with
 * `data.pages.flatMap((page) => page.nodes)`. Paging stops once the connection
 * reports no next page, and also when it reports one without an end cursor,
 * since the backend rejects an empty cursor.
 */
export function useListFossabotPRs(options: ListFossabotPRsOptions, shouldFetch = true) {
  return useInfiniteQuery<ListFossabotPRsResponse, ErrorResponse>({
    queryKey: getListFossabotPRsQueryKey(options),
    queryFn: (context) => fetchListFossabotPRs(context, options),
    getNextPageParam: (lastPage) => (
      lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor ?? undefined : undefined
    ),
    enabled: shouldFetch && !!options.projectLocator,
    /*
     * Matches app/ReactQuery/useInfiniteQuery.ts: drop the cache on unmount, so
     * a remount refetches page one instead of every page loaded before it.
     */
    cacheTime: 0,
    /*
     * Every filter is part of the key, so each settled keystroke in the search box
     * starts a query with nothing cached behind it. Without this the rows would be
     * swapped for the loading skeletons, jumping the page, on every search.
     */
    keepPreviousData: true,
  });
}
